import { useState, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Sparkles, FolderPlus, Layers } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { API } from "@/App";
import { groupLineItemsBySections } from "@/utils/lineItemSections";

export function COLineItemsCard({ lineItems, onAdd, onAddSection, onUpdate, onRemove }) {
  const [drafting, setDrafting] = useState(null);
  const groups = useMemo(() => groupLineItemsBySections(lineItems), [lineItems]);
  const hasAnySection = groups.some((g) => g.header);

  const handleDraftDescription = async (item) => {
    if (!item.service_name?.trim()) {
      toast.error("Enter a service name first");
      return;
    }
    setDrafting(item.id);
    try {
      const resp = await axios.post(`${API}/ai/draft-description`, {
        service_name: item.service_name,
        current_description: "",
      });
      onUpdate(item.id, "service_name", item.service_name);
      onUpdate(item.id, "description", resp.data.description);
      toast.success("Description drafted by AI");
    } catch (err) {
      toast.error(err.response?.data?.detail || "AI draft failed");
    } finally {
      setDrafting(null);
    }
  };

  return (
    <Card className="bg-white border border-zinc-200 shadow-sm rounded-sm">
      <CardHeader className="border-b border-zinc-100 px-6 py-4">
        <div className="flex items-center justify-between">
          <CardTitle className="font-heading text-lg uppercase tracking-tight text-[#00341d]">
            Additional Services
          </CardTitle>
          <Button
            variant="outline"
            size="sm"
            onClick={onAddSection}
            data-testid="co-add-section-btn"
            className="h-8 text-[#00341d] border-[#00341d]/30 hover:bg-[#00341d]/5"
          >
            <FolderPlus className="w-4 h-4 mr-1.5" /> Add Section
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-6 space-y-4">
        {groups.map((group, gIdx) => (
          <div
            key={group.header ? group.header.id : `co-ungrouped-${gIdx}`}
            className={
              group.header
                ? "border border-[#00341d]/20 rounded-md bg-[#00341d]/[0.02]"
                : "space-y-3"
            }
            data-testid={group.header ? `co-section-group-${group.header.id}` : "co-section-group-ungrouped"}
          >
            {group.header && (
              <div className="flex items-center gap-2 px-4 py-3 bg-[#00341d]/5 border-b border-[#00341d]/15 rounded-t-md">
                <Layers className="w-4 h-4 text-[#00341d] flex-shrink-0" />
                <Input
                  value={group.header.service_name || ""}
                  onChange={(e) => onUpdate(group.header.id, "service_name", e.target.value)}
                  placeholder="Section title"
                  className="font-heading uppercase tracking-tight text-[#00341d] bg-transparent border-none h-8 px-1 focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid={`co-section-title-input-${group.header.id}`}
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => onAdd(group.header.id)}
                  className="h-8 bg-white"
                  data-testid={`co-section-add-item-${group.header.id}`}
                >
                  <Plus className="w-3 h-3 mr-1" /> Add to section
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => onRemove(group.header.id)}
                  className="h-8 w-8 p-0 text-red-500 hover:text-red-700 hover:bg-red-50"
                  data-testid={`co-section-remove-${group.header.id}`}
                  title="Remove section"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            )}

            <div className={group.header ? "p-4 space-y-3" : "space-y-3"}>
              {group.items.length === 0 && group.header && (
                <div className="text-center py-3 text-xs text-zinc-400 border border-dashed border-zinc-200 rounded-md">
                  No items yet. Use <span className="font-medium">Add to section</span>.
                </div>
              )}

              {group.items.map((item) => (
                <div key={item.id} className="bg-zinc-50 p-4 rounded-lg space-y-3">
                  <div className="grid grid-cols-12 gap-3 items-end">
                    <div className="col-span-4">
                      <Label className="text-xs text-zinc-500">Service</Label>
                      <Input
                        value={item.service_name}
                        onChange={(e) => onUpdate(item.id, "service_name", e.target.value)}
                        placeholder="Service name"
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-zinc-500">Qty</Label>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => onUpdate(item.id, "quantity", parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-zinc-500">Unit Price</Label>
                      <Input
                        type="number"
                        value={item.unit_price}
                        onChange={(e) => onUpdate(item.id, "unit_price", parseFloat(e.target.value) || 0)}
                        className="h-9"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs text-zinc-500">Total</Label>
                      <div className="h-9 flex items-center font-medium text-[#00341d]">
                        ${item.total.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                      </div>
                    </div>
                    <div className="col-span-2 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => onRemove(item.id)}
                        className="h-9 text-red-500 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  <div className="relative">
                    <input
                      type="text"
                      value={item.description || ""}
                      onChange={(e) => onUpdate(item.id, "description", e.target.value)}
                      placeholder="Description (optional)..."
                      className="w-full h-9 border rounded-md px-3 text-sm text-zinc-500 bg-white pr-[90px] focus:outline-none focus:ring-2 focus:ring-[#00341d]/20 focus:border-[#00341d]"
                    />
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => handleDraftDescription(item)}
                      disabled={drafting === item.id}
                      data-testid={`co-draft-desc-btn-${item.id}`}
                      title="Draft description with AI"
                      className="absolute right-1 top-1 h-7 px-2 text-xs text-amber-600 hover:text-amber-700 hover:bg-amber-50 border border-amber-200 rounded-md gap-1"
                    >
                      {drafting === item.id
                        ? <><span className="w-3 h-3 border border-amber-500 border-t-transparent rounded-full animate-spin inline-block" /> Drafting...</>
                        : <><Sparkles className="w-3 h-3" /> Draft AI</>
                      }
                    </Button>
                  </div>
                </div>
              ))}

              {group.header && hasAnySection && group.items.length > 0 && (
                <div className="flex justify-end pt-1 border-t border-[#00341d]/10">
                  <div className="flex items-center gap-4 text-sm">
                    <span className="text-zinc-600">Section subtotal</span>
                    <span
                      className="font-bold text-[#00341d]"
                      data-testid={`co-section-subtotal-${group.header.id}`}
                    >
                      ${group.subtotal.toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}

        <Button variant="outline" size="sm" onClick={() => onAdd(null)} className="w-full" data-testid="co-add-line-item-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Line Item
        </Button>
      </CardContent>
    </Card>
  );
}
